import Head from 'next/head';
import { useRouter } from 'next/router';

import { isValidLocale } from './utils';

import type { LocaleTranslations } from '@/types/shared';

const baseDefaultLocale = 'en-GB';
const validLocales = ['en-GB', 'en-US'];
const host = 'https://www.multiverse.io/';

const SEOTags = ({ tagsData }) => {
  const { alternateTags, defaultHref, canonicalHref } = tagsData;
  return (
    <Head>
      {alternateTags.map((locale) => {
        return (
          <link
            key={locale.lang}
            rel="alternate"
            href={locale.href}
            hrefLang={locale.lang}
          />
        );
      })}
      <link rel="alternate" href={defaultHref} hrefLang="x-default" />
      <link rel="canonical" key="canonical" href={canonicalHref} />
    </Head>
  );
};

const AlternateTags = ({
  localeTranslations,
}: {
  localeTranslations: LocaleTranslations;
}) => {
  const router = useRouter();

  if (!router || !isValidLocale(router.locale)) {
    return null;
  }

  const routerPath = router.asPath === '/' ? '' : router.asPath;
  const tagsData = {
    alternateTags: [],
    defaultHref: '',
    canonicalHref: host + router.locale + routerPath,
  };

  if (typeof localeTranslations === 'boolean') {
    if (router.asPath == '/programmes' || router.asPath == '/programs') {
      tagsData.alternateTags.push({
        href: 'https://www.multiverse.io/en-US/programs',
        lang: 'en-US',
      });
      tagsData.alternateTags.push({
        href: 'https://www.multiverse.io/en-GB/programmes',
        lang: 'en-GB',
      });
      tagsData.defaultHref = 'https://www.multiverse.io/en-GB/programmes';
    } else {
      validLocales.forEach((locale) => {
        tagsData.alternateTags.push({
          href: host + locale + routerPath,
          lang: locale,
        });
      });
      tagsData.defaultHref = host + baseDefaultLocale + routerPath;
    }
  }

  const pathName = (type, slug) => {
    if (slug === 'home') return '';
    switch (type) {
      case 'page':
        return `/${slug}`;
      case 'infoPage':
        return `/info/${slug}`;
      case 'blogPost':
        return `/blog/${slug}`;
      case 'blogAuthor':
        return `/blog/author/${slug}`;
      case 'blogCategory':
        return `/blog/category/${slug}`;
      case 'hubspotForm':
        return `/${slug}`;
      default:
        return '';
    }
  };

  if (Array.isArray(localeTranslations)) {
    const defaultUrl = () => {
      const defaultPage =
        localeTranslations.filter(
          (locale) => locale._lang == baseDefaultLocale,
        )[0] || localeTranslations[0];
      const { _lang, slug, _type } = defaultPage;

      return host + _lang + pathName(_type, slug.current);
    };

    localeTranslations.forEach((locale) => {
      tagsData.alternateTags.push({
        href: host + locale._lang + pathName(locale._type, locale.slug.current),
        lang: locale._lang,
      });
    });
    tagsData.defaultHref = defaultUrl();
  }

  return <SEOTags tagsData={tagsData} />;
};

export { AlternateTags };
